import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import api, { apiErr } from "../lib/api";
import { formatGs } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useSucursales } from "../lib/useSucursales";
import { CheckCircle2, FileText, History, Clock } from "lucide-react";
import { toast } from "sonner";
import { CodeChip } from "../components/EnvioDetalle";
import { SearchBox, matchQ } from "../components/SearchBox";

export default function Liquidaciones() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("pendientes");
  const [suc, setSuc] = useState("todas");
  const SUCS = [{ code: "todas", name: "Todas" }, ...useSucursales().map((s) => ({ code: s.code, name: s.code }))];
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [hist, setHist] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const load = useCallback(() => { api.get(`/liquidaciones?sucursal=${suc}`).then(({ data }) => setRows(data)).catch(() => {}); }, [suc]);
  const loadHist = useCallback(() => {
    const p = new URLSearchParams({ sucursal: suc });
    if (desde) p.append("desde", desde);
    if (hasta) p.append("hasta", hasta);
    api.get(`/liquidaciones/historial?${p.toString()}`).then(({ data }) => setHist(data)).catch(() => {});
  }, [suc, desde, hasta]);

  useEffect(() => { if (tab === "pendientes") load(); else loadHist(); }, [tab, load, loadHist]);

  const cerrar = async (remitente) => {
    try { const { data } = await api.post(`/liquidaciones/cerrar`, { remitente }); toast.success(`${data.envios_liquidados} envíos liquidados`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const total = rows.reduce((s, r) => s + r.total, 0);
  const totalHist = hist.reduce((s, r) => s + r.total, 0);
  const inputCls = "rounded-lg border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-[#3B82F6]";

  return (
    <>
      <Header title="Liquidaciones" subtitle="Pago a remitentes (valor del producto)" onMenu={() => setMobileOpen(true)} />
      <main className="p-4 sm:p-6 lg:p-8 space-y-5">
        <div className="flex gap-2" data-testid="liq-tabs">
          <button onClick={() => setTab("pendientes")} data-testid="liq-tab-pendientes" className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "pendientes" ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-[#0A1F44] border-slate-300"}`}><Clock className="h-4 w-4" /> Pendientes</button>
          <button onClick={() => setTab("historial")} data-testid="liq-tab-historial" className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "historial" ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-[#0A1F44] border-slate-300"}`}><History className="h-4 w-4" /> Historial de pagos</button>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap gap-2" data-testid="liq-sucursal-filtros">
            {SUCS.map((s) => (
              <button key={s.code} onClick={() => setSuc(s.code)} data-testid={`liq-suc-${s.code}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${suc === s.code ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-[#0A1F44] border-slate-300 hover:bg-slate-50"}`}>
                {s.name}
              </button>
            ))}
          </div>
        )}

        {tab === "pendientes" ? (
          <>
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center"><FileText className="h-5 w-5 text-[#3B82F6]" /></div>
              <div><div className="text-xs text-[#64748B] uppercase">Total a liquidar{suc !== "todas" ? ` · ${suc}` : ""}</div><div className="text-2xl font-extrabold text-[#0A1F44]">{formatGs(total)}</div></div>
            </div>
            <SearchBox value={q} onChange={setQ} placeholder="Buscar remitente o código…" testid="liq-search" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rows.filter(matchQ(q)).map((r) => (
                <div key={r.remitente} className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div><div className="font-bold text-[#0A1F44]">{r.remitente}</div><div className="text-xs text-[#64748B]">{r.cantidad} entregas</div></div>
                    <div className="text-xl font-extrabold text-[#0A1F44]">{formatGs(r.total)}</div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">{r.envios.map((c) => <CodeChip key={c} code={c} onChanged={load} />)}</div>
                  <button onClick={() => cerrar(r.remitente)} data-testid={`cerrar-liq-${r.remitente}`} className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-2"><CheckCircle2 className="h-4 w-4" /> Marcar como liquidado</button>
                </div>
              ))}
              {rows.length === 0 && <div className="col-span-full text-center py-12 text-[#64748B] bg-white rounded-xl border border-[#E2E8F0]">No hay liquidaciones pendientes.</div>}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3 bg-white rounded-xl border border-[#E2E8F0] p-4 shadow-sm">
              <div><label className="block text-[11px] font-semibold text-[#64748B] mb-1">Desde</label><input type="date" className={inputCls} value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="liq-hist-desde" /></div>
              <div><label className="block text-[11px] font-semibold text-[#64748B] mb-1">Hasta</label><input type="date" className={inputCls} value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="liq-hist-hasta" /></div>
              <button onClick={loadHist} data-testid="liq-hist-filtrar" className="rounded-lg bg-[#0A1F44] text-white px-4 py-1.5 text-sm font-semibold">Filtrar</button>
              <div className="ml-auto text-right"><div className="text-[11px] text-[#64748B] uppercase">Total pagado</div><div className="text-xl font-extrabold text-emerald-600">{formatGs(totalHist)}</div></div>
            </div>
            <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[#64748B] text-xs uppercase"><tr><th className="text-left font-semibold px-4 py-3">Fecha</th><th className="text-left font-semibold px-4 py-3">Remitente</th><th className="text-center font-semibold px-4 py-3">Envíos</th><th className="text-right font-semibold px-4 py-3">Total</th><th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Liquidado por</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {hist.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50/60" data-testid={`liq-hist-row-${i}`}>
                      <td className="px-4 py-3 font-medium text-[#0A1F44]">{r.fecha}</td>
                      <td className="px-4 py-3">{r.remitente}</td>
                      <td className="px-4 py-3 text-center tabular-nums">{r.cantidad}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#0A1F44]">{formatGs(r.total)}</td>
                      <td className="px-4 py-3 hidden sm:table-cell text-[#64748B]">{r.by || "—"}</td>
                    </tr>
                  ))}
                  {hist.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#64748B]">No hay liquidaciones registradas en ese período.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </>
  );
}
